import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { useStyletron } from '@thanx/uikit/theme'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import type { PickerSubmissionPayload } from 'slices/aiConversations/pickerTypes'
import {
  createCanonicalPicker,
  PickerSliceState,
  selectGroupDisabled,
  selectGroupLevel1Id,
  selectHasAnyCanonicalPick,
  selectPickerIdsForGroup,
} from 'slices/aiSegmentBuilder/picker'

import PickerGroupHeaderContainer from '../PickerGroupHeader.container'
import CanonicalPickerCardContainer from './CanonicalPickerCard.container'
import copyKeyOf from './copyKeyOf'
import useCanonicalConfirm from './useCanonicalConfirm'

const t = buildTranslate('components.ai.picker.canonical')

type RootState = { picker: PickerSliceState }

type Props = {
  groupId: string
  // The picker config the response answers
  pickerType: string
  onSubmit: (payload: PickerSubmissionPayload) => void
}

/** A canonical picker: its cards, a way to add one, and Confirm */
const CanonicalPickerGroupContainer = ({
  groupId,
  pickerType,
  onSubmit,
}: Props) => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const pickerIds = useSelector(
    (state: RootState) => selectPickerIdsForGroup(state, groupId),
    shallowEqual
  )
  const disabled = useSelector((state: RootState) =>
    selectGroupDisabled(state, groupId)
  )
  const level1Id = useSelector((state: RootState) =>
    selectGroupLevel1Id(state, groupId)
  )
  const hasAnyPick = useSelector((state: RootState) =>
    selectHasAnyCanonicalPick(state, groupId)
  )
  const { confirm, isTooLarge } = useCanonicalConfirm(
    groupId,
    pickerType,
    onSubmit
  )

  const canRemove = pickerIds.length > 1

  return (
    <div
      className={`mb-l p-m rounded ${css({
        border: `1px solid ${palette.grey30}`,
        backgroundColor: palette.grey10,
      })}`}
    >
      <PickerGroupHeaderContainer groupId={groupId} disabled={disabled} />
      <div className="mb-m">
        {pickerIds.map((pickerId, index) => (
          <CanonicalPickerCardContainer
            key={pickerId}
            pickerId={pickerId}
            index={index}
            canRemove={canRemove}
            disabled={disabled}
          />
        ))}
      </div>
      {isTooLarge && (
        <div className="mb-m">
          <Notification kind="negative" size="full">
            {t('too_large')}
          </Notification>
        </div>
      )}
      <div className="d-flex justify-content-between align-items-center">
        <Button
          kind="secondary"
          disabled={disabled}
          onClick={() => dispatch(createCanonicalPicker({ groupId }))}
        >
          {t(`${copyKeyOf(level1Id)}.add`)}
        </Button>
        <Button
          kind="primary"
          disabled={disabled || !hasAnyPick}
          onClick={confirm}
        >
          {t('confirm')}
        </Button>
      </div>
    </div>
  )
}

export default CanonicalPickerGroupContainer
